import { useState } from "react";
import Icon from "../../components/Icon";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import Pagination from "../../components/Pagination";
import { useApi } from "../../hooks/useApi";
import {
  listAnnouncements,
  createAnnouncement,
  updateAnnouncement,
  deleteAnnouncement,
} from "../../api/announcementService";
import { paginatedRows, paginatedMeta } from "../../utils/pagination";
import { formatDate } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";

export default function AnnouncementsPage() {
  const [page, setPage] = useState(1);
  const list = useApi(() => listAnnouncements({ page }), [page]);
  const [editing, setEditing] = useState(null); // row | "new" | null

  const rows = paginatedRows(list.data);
  const meta = paginatedMeta(list.data);

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"? This can't be undone.`)) return;
    try {
      await deleteAnnouncement(a.id);
      showToast("success", "Announcement deleted");
      list.reload();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Announcements</h1>
          <div className="page-sub">Changelog releases and info popups shown to every firm's admins.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => setEditing("new")}>
            <Icon name="plus" size={14} /> New announcement
          </button>
        </div>
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {list.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
          ) : rows.length === 0 ? (
            <EmptyState icon="sparkles" title="No announcements yet" hint="Create a release note or an info popup." />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>Popup</th>
                  <th className="t-num">Published</th>
                  <th style={{ width: 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((a) => (
                  <tr key={a.id} className="clickable" onClick={() => setEditing(a)}>
                    <td className="t-primary fw-500">{a.title}</td>
                    <td>
                      <span className={`badge ${a.type === "release" ? "badge-info" : "badge-neutral"}`}>
                        {a.type === "release" ? "Release" : "Info"}
                      </span>
                    </td>
                    <td className="t-muted">{a.version ? `v${a.version}` : "—"}</td>
                    <td>
                      {a.is_published
                        ? <span className="badge badge-success">Published</span>
                        : <span className="badge badge-neutral">Draft</span>}
                    </td>
                    <td>{a.show_popup ? <Icon name="check" size={13} /> : <span className="t-muted">—</span>}</td>
                    <td className="t-num t-muted">{a.published_at ? formatDate(a.published_at) : "—"}</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="row gap-sm">
                        <button className="icon-btn" title="Edit" onClick={() => setEditing(a)}>
                          <Icon name="edit" size={13} />
                        </button>
                        <button className="icon-btn" title="Delete" style={{ color: "var(--danger)" }} onClick={() => remove(a)}>
                          <Icon name="trash" size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <Pagination meta={meta} page={page} onPageChange={setPage} disabled={list.loading} />
        </div>
      </div>

      {editing && (
        <AnnouncementDrawer
          key={editing === "new" ? "new" : editing.id}
          announcement={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); list.reload(); }}
        />
      )}
    </>
  );
}

function AnnouncementDrawer({ announcement, onClose, onSaved }) {
  const isEdit = !!announcement;
  const [form, setForm] = useState(() => ({
    type: announcement?.type ?? "release",
    version: announcement?.version ?? "",
    title: announcement?.title ?? "",
    body: announcement?.body ?? "",
    is_published: announcement?.is_published ?? false,
    show_popup: announcement?.show_popup ?? true,
  }));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        type: form.type,
        version: form.version.trim() || null,
        title: form.title.trim(),
        body: form.body,
        is_published: form.is_published,
        show_popup: form.show_popup,
      };
      if (isEdit) {
        await updateAnnouncement(announcement.id, payload);
        showToast("success", "Announcement updated");
      } else {
        await createAnnouncement(payload);
        showToast("success", "Announcement created");
      }
      onSaved?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? "Edit announcement" : "New announcement"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.title.trim() || !form.body.trim()}>
            {saving ? "Saving…" : isEdit ? "Save" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Type</label>
          <select className="select" value={form.type} onChange={(e) => set({ type: e.target.value })}>
            <option value="release">Release (changelog)</option>
            <option value="info">Info</option>
          </select>
        </div>
        <div className="field">
          <label className="field-label">Version <span className="t-muted text-xs">(releases)</span></label>
          <input className="input" value={form.version} onChange={(e) => set({ version: e.target.value })} placeholder="e.g. 1.2.0" />
        </div>
        <div className="field">
          <label className="field-label">Title</label>
          <input className="input" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="What changed" autoFocus />
        </div>
        <div className="field">
          <label className="field-label">Body</label>
          <textarea className="textarea" rows={7} value={form.body} onChange={(e) => set({ body: e.target.value })} placeholder="Details — line breaks are preserved." />
        </div>
        <div className="field">
          <label className="row gap-sm" style={{ cursor: "pointer" }}>
            <input type="checkbox" checked={form.is_published} onChange={(e) => set({ is_published: e.target.checked })} />
            Published <span className="t-muted text-xs">(visible to firm admins)</span>
          </label>
        </div>
        <div className="field">
          <label className="row gap-sm" style={{ cursor: "pointer" }}>
            <input type="checkbox" checked={form.show_popup} onChange={(e) => set({ show_popup: e.target.checked })} />
            Show as one-time popup
          </label>
        </div>
      </div>
    </Drawer>
  );
}
